import { cancellationMessageBody } from "components/cancellation-message-choice/cancellation-message-choice.util";
import {
  convertAppointmentResponseToModel,
  convertToAppointmentInput
} from "utils/appointments.util";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { createPaginatedEndpoint } from "utils/services.util";

import { emptySplitApi } from "./empty-split-api";
import { EMPTY_STAFF_ID } from "./events";
import {
  Appointment,
  AppointmentCounters,
  AppointmentCountersResponse,
  AppointmentRecurrenceInput,
  AppointmentResponse,
  AppointmentSkeleton,
  AppointmentsDownloadQuery,
  AppointmentsResponse,
  DeleteAppointmentInput,
  NewAppointmentsResponse,
  PatientAppointmentsFilters,
  QuerySearchFilter,
  ReassignableAppointmentsPreview,
  RoomValidationRequest,
  RoomValidationResponse
} from "./types/appointments.type";
import { GoogleCalendarSyncInput } from "./types/calendar-integration.type";
import { Diagnosis } from "./types/diagnosis.type";
import { CSVReportResponse } from "./types/reports.type";
import { SchedulerAnswer } from "./types/web-scheduler-configurations.type";

// Notifications checkbox logic (notifications-and-intake-forms-field.component.tsx)
// reads phone/email straight off this fragment - any query that seeds the appointment
// form's patient (create, reschedule) must request these fields or the checkbox
// force-disables itself. See SN-18966.
const PATIENT_APPOINTMENT_FIELDS =
  "id,first_name,last_name,title,suffix,preferred_name,middle_name,photo,phone,phone_type,email,birthdate,preferred_communication_method,do_not_email,do_not_message,do_not_call,do_not_mail";

/** RestQL query of the patient Appointments tabs (`getPatientAppointmentsPaginated`). */
export const PATIENT_APPOINTMENTS_LIST_QUERY = `{id,is_opportunity,migration_source_id,schedule,notes,replacement_appointment{id},needs_reschedule,patient_feedback_rating,outcome,outcome_reason,title,outcome_notes,start_moment,end_moment,status,referral_source{id,name},staff_member{id,first_name,last_name,suffix,title,is_active},room{clinic{id,name,logo,practice{id,name,logo}}},clinic{id,timezone,name,logo,practice{id,name,logo}},type{id,name,default_duration,is_opportunity,general_type,color},assigned_tags,patient{${PATIENT_APPOINTMENT_FIELDS}},should_notify,notification_method,notify_both_channels}`;

/**
 * Discarded Appointments tab: the list query plus the discard timestamp and who discarded it
 * (`updated_by` is written by the backend at discard time — the same fields the Discarded
 * Appointments report reads).
 */
export const DISCARDED_PATIENT_APPOINTMENTS_LIST_QUERY =
  PATIENT_APPOINTMENTS_LIST_QUERY.replace(
    /}$/,
    ",discarded_at,discarded_at_display,updated_by}"
  );

const APPOINTMENT_DEFAULT_QUERY = `{id,is_opportunity,podium_id,podium_sync,title,assigned_tags,rescheduled_appointment_id,needs_reschedule,patient_feedback_rating,outcome,outcome_reason,outcome_notes,start_moment,end_moment,status,companion_name,companion_type,notes,physician_referral,referral_source{id,name},sub_referral_source{id,name},referring_physician,staff_member{id,first_name,last_name,suffix,title,is_active},is_direct_supervisor,supervising_provider{id,first_name,last_name,suffix,title,is_active},room{id,name,clinic{id,name,practice{id,name,logo}}},clinic{id,timezone,name,practice{id,name,logo}},type{id,name,default_duration,color,is_opportunity,general_type,generate_sale},chart_note,crud_events,should_create_schedule_for_staff,should_create_intake_form_request,patient{${PATIENT_APPOINTMENT_FIELDS}},diagnosis,hearing_test_conducted,companion_present,hearing_aid_demo_performed,telehealth_provider,zoom_join_url,zoom_meeting_id,should_notify,notification_method,notify_both_channels,timer{id,status,status_display,minutes_accumulated,last_started_at,completed},updated_at}`;

// Room moments stay OUT of the default query: django-restql raises FieldNotFound (400) on a
// backend that predates them, which would break an FE-first deploy. Callers that need the room
// window opt in behind the ROOM_BOOKING_DURATION_OVERRIDE flag.
export const withRoomMoments = (
  enabled: boolean,
  query = APPOINTMENT_DEFAULT_QUERY
) =>
  enabled ? `${query.slice(0, -1)},room_start_moment,room_end_moment}` : query;

/** Why a zoom-sync request was refused. Both are fixed in Settings, not by retrying. */
export type ZoomSyncErrorCode = "ZOOM_DISABLED" | "ZOOM_NOT_CONNECTED";

export type ZoomSyncErrorBody = {
  code?: ZoomSyncErrorCode;
  message?: string;
  detail?: string;
};

export const appointmentsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    queryAppointmentsCounter: builder.query<
      AppointmentCounters,
      Omit<QuerySearchFilter, "query" | "limit" | "offset">
    >({
      query: ({ staff_member, clinic, ...filters }) =>
        `/appointments/counters/?${formatValidQueryParamUrlCommaSeparated({
          ...filters,
          clinic: clinic?.join(",") || undefined,
          staff_member: staff_member?.join(",") || EMPTY_STAFF_ID.toString(),
          status: undefined
        })}`,
      transformResponse: (response: AppointmentCountersResponse) => ({
        appointmentAll: response.appointment_all ?? 0,
        appointmentAllNoDrafts: response.appointment_all_no_drafts ?? 0,
        appointmentDraft: response.appointment_draft ?? 0,
        appointmentUnconfirmed: response.appointment_unconfirmed ?? 0,
        appointmentOpportunity: response.appointment_opportunity ?? 0,
        appointmentIncomplete: response.appointment_incomplete ?? 0,
        appointmentCancelledRescheduled:
          response.appointment_cancelled_rescheduled ?? 0
      }),
      providesTags: ["AppointmentCounters", "Appointments", "Events"]
    }),
    queryAppointmentsWithEmailsAndPhoneCount: builder.query<
      { count: number; count_sms: number; count_distinct_patients: number },
      any
    >({
      query: (filters) =>
        `/appointments/patients-count/${formatValidQueryParamUrlCommaSeparated(
          filters,
          true
        )}`,
      providesTags: ["Appointments"],
      transformResponse: (response: {
        count: number;
        count_sms: number;
        count_distinct_patients: number;
      }) => ({
        count: Number(response.count),
        count_sms: Number(response.count_sms),
        count_distinct_patients: Number(response.count_distinct_patients)
      })
    }),
    getAppointments: builder.query<
      AppointmentsResponse,
      Partial<PatientAppointmentsFilters>
    >({
      query: ({
        offset = 0,
        limit = 25,
        patientId,
        baseFilter,
        ordering = "-start_moment",
        query = "{id,migration_source_id,notes,needs_reschedule,start_moment,status,referral_source{id,name},staff_member{id,first_name,last_name,suffix,title,is_active},clinic{id,timezone,name,logo,practice{id,name,logo}},patient{id,first_name,last_name},type{id,name,default_duration,color},extra,physician_referral,outcome,outcome_reason,outcome_notes,patient_feedback_rating,should_create_schedule_for_staff,should_create_intake_form_request}",
        ...filters
      }) =>
        `/appointments/?limit=${limit}&offset=${offset}&ordering=${ordering}&${formatValidQueryParamUrlCommaSeparated(
          {
            ...baseFilter,
            ...filters,
            patient: patientId,
            query
          }
        )}`,
      providesTags: ["Appointments"],
      transformResponse: (
        response: AppointmentResponse[] | NewAppointmentsResponse
      ) => {
        if ("count" in response) {
          return response.results.map(convertAppointmentResponseToModel);
        }

        return response.map(convertAppointmentResponseToModel);
      }
    }),
    getReportAppointments: builder.query<
      AppointmentsResponse,
      Partial<PatientAppointmentsFilters>
    >({
      query: ({
        offset = 0,
        limit = 25,
        upToDate,
        fromDate,
        patientId,
        baseFilter,
        ordering = "-start_moment",
        query = "{id,notes,rescheduled_appointment_id,needs_reschedule,start_moment,status,referral_source{id,name},staff_member{id,first_name,last_name,suffix,title,is_active},clinic{id,timezone,name,logo,practice{id,name,logo}},patient{id,first_name,last_name},type{id,name,default_duration},extra,physician_referral,outcome,outcome_reason,outcome_notes,patient_feedback_rating,should_create_schedule_for_staff,should_create_intake_form_request}",
        ...filters
      }) =>
        `/appointments-report/?limit=${limit}&offset=${offset}&ordering=${ordering}&${formatValidQueryParamUrlCommaSeparated(
          {
            ...baseFilter,
            ...filters,
            patient: patientId,
            query
          }
        )}`,
      providesTags: ["Appointments"],
      transformResponse: (
        response: AppointmentResponse[] | NewAppointmentsResponse
      ) => {
        if ("count" in response) {
          return response.results.map(convertAppointmentResponseToModel);
        }

        return response.map(convertAppointmentResponseToModel);
      }
    }),
    getPatientAppointmentsPaginated: createPaginatedEndpoint(builder, {
      queryFn: ({
        offset = 0,
        limit = 25,
        upToDate,
        patientId,
        fromDate,
        baseFilter,
        ordering = "-start_moment",
        query = PATIENT_APPOINTMENTS_LIST_QUERY,
        ...filters
      }) =>
        `/appointments/?limit=${limit}&offset=${offset}&ordering=${ordering}&after_moment=${
          fromDate ?? ""
        }&before_moment=${
          upToDate ?? ""
        }&${formatValidQueryParamUrlCommaSeparated({
          ...baseFilter,
          ...filters,
          patient: patientId,
          query
        })}`,
      providesTags: ["Appointments"],
      transformResponseFn: (
        response: AppointmentResponse[] | NewAppointmentsResponse
      ) => {
        if ("count" in response) {
          return response.results.map(convertAppointmentResponseToModel);
        }

        return response.map(convertAppointmentResponseToModel);
      }
    }),
    getPatientAppointments: builder.query<
      AppointmentsResponse,
      PatientAppointmentsFilters
    >({
      query: ({
        offset = 0,
        limit = 25,
        upToDate,
        patientId,
        fromDate,
        baseFilter,
        ordering = "-start_moment",
        query = "{id,notes,replacement_appointment{id},patient_feedback_rating,outcome,outcome_reason,title,outcome_notes,start_moment,end_moment,status,referral_source{id,name},staff_member{id,first_name,last_name,suffix,title,is_active},room{clinic{id,name,logo,practice{id,name,logo}}},clinic{id,timezone,name,logo,practice{id,name,logo}},type{id,name,default_duration,is_opportunity,general_type},assigned_tags}",
        ...filters
      }) =>
        `/appointments/?limit=${limit}&offset=${offset}&ordering=${ordering}&after_moment=${
          fromDate ?? ""
        }&before_moment=${
          upToDate ?? ""
        }&${formatValidQueryParamUrlCommaSeparated({
          ...baseFilter,
          ...filters,
          patient: patientId,
          query
        })}`,

      providesTags: (result = []) =>
        result?.map((appointment) => ({
          type: "Appointments" as const,
          id: appointment.id
        })),

      transformResponse: (
        response: AppointmentResponse[] | NewAppointmentsResponse
      ) => {
        if ("count" in response) {
          return response.results.map(convertAppointmentResponseToModel);
        }

        return response.map(convertAppointmentResponseToModel);
      }
    }),
    getAppointment: builder.query<Appointment, { id: number; query?: string }>({
      query: ({ id, query = APPOINTMENT_DEFAULT_QUERY }) =>
        `/appointments/${id}/?query=${query}`,
      providesTags: (result) => [
        {
          type: "Appointments" as const,
          id: result?.id
        }
      ],
      transformResponse: convertAppointmentResponseToModel
    }),
    /**
     * The patient behind an appointment, and nothing else.
     *
     * Sales Coach carries `appointment_id` but no patient id, and the appointment page
     * lives at `/patients/:patientId/appointments/:appointmentId` — under the patient
     * chart — so its link cannot be built without one hop.
     *
     * Deliberately NOT `getAppointment`: that runs `convertAppointmentResponseToModel`
     * over a ~40-key default projection when all that is wanted here is one integer.
     * Its result is also raw, so nothing downstream can mistake it for an `Appointment`.
     */
    getAppointmentPatientId: builder.query<
      { id: number; patient: { id: number } | null },
      number
    >({
      query: (id) => `/appointments/${id}/?query={id,patient{id}}`,
      keepUnusedDataFor: 300
    }),
    /**
     * The online-scheduler answers stored on an appointment (SN-19962), and nothing else.
     *
     * Same shape as `getAppointmentPatientId`: raw, narrow, untagged. The answers are written once
     * at booking and never change, and keeping the field out of the shared `Appointment` object
     * means no appointment form can PATCH it back. Request it only with
     * WEB_SCHEDULER_CONFIGURATIONS on: an older backend rejects the unknown RestQL field.
     */
    getAppointmentSchedulerAnswers: builder.query<
      { id: number; webscheduler_custom_fields?: SchedulerAnswer[] | null },
      number
    >({
      query: (id) =>
        `/appointments/${id}/?query={id,webscheduler_custom_fields}`,
      keepUnusedDataFor: 300
    }),
    addAppointment: builder.mutation<AppointmentSkeleton, Partial<Appointment>>(
      {
        query: (appointmentData) => ({
          url: "/appointments/",
          method: "POST",
          body: convertToAppointmentInput(appointmentData)
        }),
        invalidatesTags: (result, _error, arg) => [
          "Appointments",
          "Events",
          "Patients",
          "StaffAvailability",
          "SubReferralSources",
          "RoomTraffic",
          "OnboardingFormRequest",
          ...(result?.patient
            ? [{ type: "Patients" as const, id: result.patient }]
            : arg.patient?.id
              ? [{ type: "Patients" as const, id: arg.patient.id }]
              : [])
        ]
      }
    ),
    updateAppointment: builder.mutation<
      AppointmentSkeleton,
      Partial<Omit<Appointment, "diagnosis">> &
        Partial<{
          diagnosis: Partial<Diagnosis>;
        }> &
        AppointmentRecurrenceInput & {
          /** Staff's answer to "Send cancellation message?"; omit unless they were asked. */
          send_cancellation_message?: boolean;
        }
    >({
      query: ({ id, ...appointmentData }) => ({
        url: `/appointments/${id}/`,
        method: "PATCH",
        body: convertToAppointmentInput(appointmentData)
      }),
      invalidatesTags: (result, _error, arg) => [
        "Appointments",
        "Events",
        "Patients",
        "ChartNotes",
        "StaffAvailability",
        "SubReferralSources",
        "RoomTraffic",
        "AssignedAppointmentTags",
        "OnboardingFormRequest",
        ...(result?.patient
          ? [
              { type: "Patients" as const, id: result.patient },
              {
                type: "ActiveVisitChecklists" as const,
                id: `LIST-${result.patient}`
              }
            ]
          : arg.patient?.id
            ? [
                { type: "Patients" as const, id: arg.patient.id },
                {
                  type: "ActiveVisitChecklists" as const,
                  id: `LIST-${arg.patient.id}`
                }
              ]
            : [])
      ]
    }),
    syncAppointmentsToGoogle: builder.mutation<
      {
        status: string;
        params: GoogleCalendarSyncInput;
      },
      GoogleCalendarSyncInput
    >({
      query: (params) => ({
        url: "/appointments/google-calendar/sync/",
        method: "POST",
        body: params
      })
    }),
    syncAppointmentToGoogle: builder.mutation<string, number>({
      query: (id) => ({
        url: `/appointments/${id}/sync/`,
        method: "PATCH"
      })
    }),
    // 202 only means the sync was queued. It used to mean that unconditionally, so a clinic with
    // no Zoom connection got "queued" and then nothing, forever. The endpoint now rejects the two
    // preconditions up front with a code; show `message` instead of a success toast on a 400.
    zoomSyncAppointment: builder.mutation<{ detail: string }, number>({
      query: (id) => ({
        url: `/appointments/${id}/zoom-sync/`,
        method: "POST"
      })
    }),
    // Mints a short-lived Zoom *host* start link (SN-19531). Deliberately a mutation rather
    // than a query: whoever holds this URL is the host of the visit, so it must never be
    // cached by RTK, and the endpoint has the side effect of recording who took the host seat.
    // The timeout keeps a hanging Zoom call from stranding a provider mid-visit — the shared
    // `retry()` wrapper only retries GETs, so this is never re-issued behind the user's back.
    getZoomStartLink: builder.mutation<{ url: string }, number>({
      query: (id) => ({
        url: `/appointments/${id}/zoom-start-link/`,
        method: "POST",
        timeout: 10000
      }),
      // `useZoomJoin` tells the provider what failed and that the guest link opened instead
      // (ENG-2085); the generic timeout toast on top of that would be a second, vaguer message.
      extraOptions: { suppressGlobalErrorToast: true }
    }),
    deleteAppointment: builder.mutation<string, DeleteAppointmentInput>({
      query: ({
        appointmentId,
        start_recurrence_id,
        send_cancellation_message
      }) => ({
        url: `/appointments/${appointmentId}/`,
        method: "DELETE",
        body: {
          start_recurrence_id,
          ...cancellationMessageBody(send_cancellation_message)
        }
      }),
      invalidatesTags: [
        "Appointments",
        "Events",
        "StaffAvailability",
        "RoomTraffic"
      ]
    }),
    bulkDeleteAppointments: builder.mutation<string, { ids: number[] }>({
      query: ({ ids }) => ({
        url: "/appointments/bulk-delete/",
        method: "DELETE",
        body: { ids }
      }),
      invalidatesTags: [
        "Appointments",
        "Events",
        "StaffAvailability",
        "RoomTraffic"
      ]
    }),
    getReassignableAppointmentsPreview: builder.query<
      ReassignableAppointmentsPreview,
      {
        from_staff_member: number;
        from_date: string;
        limit?: number;
        offset?: number;
        // List filters only — they never change what a move covers.
        search?: string;
        clinic?: number;
      }
    >({
      // `search` is free text: `&`, `#` or `+` in a name would break the query string (review D10).
      query: ({ search, ...params }) =>
        `/appointments/bulk-reassign-provider/preview/?${formatValidQueryParamUrlCommaSeparated(
          {
            ...params,
            search: search ? encodeURIComponent(search) : undefined
          }
        )}`,
      providesTags: ["Appointments"]
    }),
    reassignProviderAppointments: builder.mutation<
      // bulk_action_id: the run's row in Settings > Bulk Action History (undoable there).
      { reassigned_count: number; bulk_action_id: number },
      {
        from_staff_member: number;
        to_staff_member: number;
        from_date: string;
        // Partial move: the preview rows the admin unticked.
        exclude_appointment_ids?: number[];
        // Follow-ups: the backend defaults notify_patients off and sync_integrations on.
        notify_patients?: boolean;
        sync_integrations?: boolean;
      }
    >({
      query: (body) => ({
        url: "/appointments/bulk-reassign-provider/",
        method: "POST",
        body
      }),
      invalidatesTags: (
        _result,
        _error,
        { from_staff_member, to_staff_member }
      ) => [
        "Appointments",
        "Events",
        "StaffAvailability",
        "RoomTraffic",
        // The run lands in Settings > Bulk Action History (SN-19777).
        "BulkActions",
        // Both rows' future_appointments_count on the admin panel's users list (SN-19777).
        { type: "Staff", id: from_staff_member },
        { type: "Staff", id: to_staff_member }
      ]
    }),
    validateRoomAvailability: builder.mutation<
      RoomValidationResponse,
      RoomValidationRequest
    >({
      query: (body) => ({
        url: "/appointments/",
        method: "POST",
        body
      })
    }),
    downloadAppointmentsCSV: builder.query<
      CSVReportResponse,
      AppointmentsDownloadQuery
    >({
      query: ({ query = "{*}", csvHeader = "*", csvLabels, ...filters }) => ({
        url: `/appointments/?ordering=-start_moment&query=${query}&csv_header=${csvHeader}&${formatValidQueryParamUrlCommaSeparated(
          {
            csv_labels: csvLabels,
            ...filters
          }
        )}`,
        headers: { Accept: "custom-csv/json" }
      })
    })
  })
});

export const {
  useLazyQueryAppointmentsCounterQuery,
  useAddAppointmentMutation,
  useDeleteAppointmentMutation,
  useUpdateAppointmentMutation,
  useGetPatientAppointmentsQuery,
  useGetAppointmentsQuery,
  useLazyGetAppointmentsQuery,
  useGetReportAppointmentsQuery,
  useGetAppointmentQuery,
  useLazyGetAppointmentQuery,
  useLazyGetAppointmentPatientIdQuery,
  useGetAppointmentSchedulerAnswersQuery,
  useLazyDownloadAppointmentsCSVQuery,
  useBulkDeleteAppointmentsMutation,
  useSyncAppointmentsToGoogleMutation,
  useSyncAppointmentToGoogleMutation,
  useQueryAppointmentsWithEmailsAndPhoneCountQuery,
  useValidateRoomAvailabilityMutation,
  useZoomSyncAppointmentMutation,
  useGetZoomStartLinkMutation,
  useReassignProviderAppointmentsMutation,
  useGetReassignableAppointmentsPreviewQuery
} = appointmentsApi;
